import { faCircleInfo, faDisplay, faReceipt } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { BillingService } from "src/app/services/Index";
import "./AdminBilling.css";

type Props = {};
type State = {
	plan: any;
	summary: any;
	isLoading: boolean;
};

const money = (pence: number) =>
	`£${((Number(pence) || 0) / 100).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const pct = (value: number) => `${(Number(value) || 0).toFixed(2).replace(/\.00$/, "")}%`;

/**
 * What the club pays.
 *
 * Read-only, and not because we haven't got round to the edit screen — rates
 * are commercial terms. The club sees exactly what it is charged and can
 * reconcile a payout to the penny; changing it is a conversation, not a button.
 */
export class AdminBilling extends React.Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = { plan: null, summary: null, isLoading: true };
	}

	componentDidMount() {
		this.load();
	}

	async load() {
		try {
			const [plan, summary] = await Promise.all([
				(await BillingService.getPlan()).json(),
				(await BillingService.getSummary()).json()
			]);
			this.setState({ plan, summary, isLoading: false });
		} catch {
			this.setState({ isLoading: false });
		}
	}

	/** A worked example beats a percentage — it's what the club will actually check. */
	renderExample() {
		const { plan } = this.state;
		if (!plan?.ticketing?.length) return null;

		const rate = Number(plan.ticketing[0].percent) || 0;
		const taxRate = Number(plan.feeTaxPercent) || 0;
		const gross = 10000;                                   // £100.00
		const fee = Math.round(gross * rate / 100);
		const tax = Math.round(fee * taxRate / 100);

		return (
			<div className="bl-example">
				<h4>On a £100 sale</h4>
				<div className="bl-example-row"><span>Ticket sales</span><span>{money(gross)}</span></div>
				<div className="bl-example-row"><span>Our fee ({pct(rate)})</span><span>&minus;{money(fee)}</span></div>
				<div className="bl-example-row"><span>VAT on our fee ({pct(taxRate)})</span><span>&minus;{money(tax)}</span></div>
				<div className="bl-example-row bl-example-total"><span>You receive</span><span>{money(gross - fee - tax)}</span></div>
				<p className="bl-note">
					Card processing is charged separately by Stripe, straight to your account &mdash;
					it never passes through us.
				</p>
			</div>
		);
	}

	render() {
		const { plan, summary, isLoading } = this.state;

		if (isLoading) return <div style={{ padding: 20 }}><h2>Billing</h2><p style={{ color: "var(--pb-text-muted)" }}>Loading&hellip;</p></div>;
		if (!plan?.available) {
			return (
				<div style={{ padding: 20 }}>
					<h2 style={{ marginBottom: 20 }}>Billing</h2>
					<div className="bl-empty"><FontAwesomeIcon icon={faReceipt} /><p>Billing isn&rsquo;t available for this club yet.</p></div>
				</div>
			);
		}

		return (
			<div style={{ padding: 20 }}>
				<h2 style={{ marginBottom: 6 }}>Billing</h2>
				<p style={{ color: "var(--pb-text-muted)", marginBottom: 24 }}>
					Your plan and what you&rsquo;ve been charged. To change any of this, talk to us.
				</p>

				<div className="bl-grid">
					<div className="bl-card bl-scoreboard">
						<div className="bl-card-head"><FontAwesomeIcon icon={faDisplay} /><span>Scoreboard</span></div>
						<div className="bl-price">{money(plan.scoreboard.monthlyPence)}<small>/month</small></div>
						<p className="bl-note">{plan.scoreboard.description}</p>
					</div>

					<div className="bl-card">
						<div className="bl-card-head"><FontAwesomeIcon icon={faReceipt} /><span>Ticketing</span></div>
						{plan.ticketing.map((line: any) => (
							<div className="bl-rate-row" key={`t-${line.name}`}>
								<span>{line.name}</span><strong>{pct(line.percent)}</strong>
							</div>
						))}
					</div>

					<div className="bl-card">
						<div className="bl-card-head"><FontAwesomeIcon icon={faReceipt} /><span>Other</span></div>
						{plan.other.map((line: any) => (
							<div className="bl-rate-row" key={`o-${line.name}`}>
								<span>{line.name}{line.note && <small>{line.note}</small>}</span>
								<strong>{pct(line.percent)}</strong>
							</div>
						))}
					</div>
				</div>

				{this.renderExample()}

				{(plan.notes || []).length > 0 &&
					<div className="bl-notes">
						<FontAwesomeIcon icon={faCircleInfo} />
						<ul>{plan.notes.map((n: string, i: number) => <li key={`n-${i}`}>{n}</li>)}</ul>
					</div>}

				<h3 style={{ margin: "32px 0 12px" }}>This month</h3>
				{!summary?.lines?.length &&
					<p style={{ color: "var(--pb-text-muted)" }}>No sales yet this month.</p>}

				{summary?.lines?.length > 0 &&
					<div className="bl-table">
						<div className="bl-tr bl-th">
							<span>Channel</span><span>Orders</span><span>Gross</span><span>Rate</span><span>Our fee</span><span>VAT</span><span>You keep</span>
						</div>
						{summary.lines.map((line: any) => (
							<div className="bl-tr" key={`s-${line.channel}`}>
								<span>{line.channel}</span>
								<span>{line.orders}</span>
								<span>{money(line.grossPence)}</span>
								<span>{pct(line.ratePercent)}</span>
								<span>{money(line.feePence)}</span>
								<span>{money(line.feeTaxPence)}</span>
								<span>{money(line.netPence)}</span>
							</div>
						))}
						<div className="bl-tr bl-tf">
							<span>Total</span><span /><span>{money(summary.grossPence)}</span><span />
							<span>{money(summary.feePence)}</span><span>{money(summary.feeTaxPence)}</span>
							<span>{money(summary.netPence)}</span>
						</div>
					</div>}
			</div>
		);
	}
}
